import { Fragment, useEffect, useRef, useState } from 'react';
import { View } from 'foundations-components/components/View';
import { withErrorBoundary } from '../ErrorBoundary';
import { FeatureMonetizationTrackerProvider, useFeatureMonetizationTracker } from '../FeatureMonetizationTrackerProvider';
import { useIntersectionObserver } from '../useIntersectionObserver';
import { CategorizedFeatures } from './CategorizedFeatures';
import { useFocusedCategory } from './category-header/useFocusedCategory';
import { useTableHeaderConfig } from './category-header/useTableHeaderConfig';
import { useFeatureCategories, useProducts } from './data-cache/dataCacheHooks';
import { FeatureTableDataCacheProvider } from './data-cache/FeatureTableDataCache';
import { FeatureTableSessionCacheProvider } from './FeatureTableSessionCacheProvider';
import { ProductServiceCatalogPrompt } from './ProductServiceCatalogPrompt';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const FeatureTable = withErrorBoundary(FeatureTableWithProviders({
  isPublicUseCase: false
}), {
  componentName: 'FeatureTable'
});
export const InAppFeatureTable = withErrorBoundary(FeatureTableWithProviders({
  isPublicUseCase: false
}), {
  componentName: 'InAppFeatureTable'
});
export const PublicFeatureTable = withErrorBoundary(FeatureTableWithProviders({
  isPublicUseCase: true
}), {
  componentName: 'PublicFeatureTable'
});
function FeatureTableWithProviders({
  isPublicUseCase
}) {
  return ({
    isUpgradePoint = false,
    onPurchaseMotionButtonClick,
    productApiNames,
    purchaseMotionLocation,
    revenueAttributionUniqueId,
    screen,
    skuCategoryName,
    usageTracker,
    StyledWrapper,
    term,
    displayOptions = {
      showPrice: true,
      showPurchaseMotion: true
    }
  }) => {
    return /*#__PURE__*/_jsx(FeatureTableSessionCacheProvider, {
      isUpgradePoint: isUpgradePoint,
      onPurchaseMotionButtonClick: onPurchaseMotionButtonClick,
      productApiNames: productApiNames,
      purchaseMotionLocation: purchaseMotionLocation || 'MERCH_CARD',
      revenueAttributionUniqueId: revenueAttributionUniqueId,
      screen: screen,
      skuCategoryName: skuCategoryName,
      term: term,
      displayOptions: displayOptions,
      children: /*#__PURE__*/_jsx(FeatureTableDataCacheProvider, {
        isPublicUseCase: isPublicUseCase,
        children: /*#__PURE__*/_jsx(FeatureMonetizationTrackerProvider, {
          tracker: usageTracker,
          children: /*#__PURE__*/_jsx(FeatureTableComponent, {
            skuCategoryName: skuCategoryName,
            StyledWrapper: StyledWrapper
          })
        })
      })
    });
  };
}
function FeatureTableComponent({
  skuCategoryName,
  StyledWrapper = Fragment
}) {
  const [visibleCategories, setVisibleCategories] = useState([]);
  const featureTableRef = useRef(null);
  const tracker = useFeatureMonetizationTracker();
  const featureCategories = useFeatureCategories() || [];
  useIntersectionObserver({
    onIntersect: () => {
      tracker.track({
        action: 'on-feature-table-scrolled-in-view',
        object: 'feature-table',
        skuCategoryName
      });
    },
    targetRef: featureTableRef
  });
  useEffect(() => {
    if (skuCategoryName) {
      setVisibleCategories([]);
    }
  }, [skuCategoryName]);
  const focusedCategory = useFocusedCategory({
    allCategories: featureCategories.map(category => category.category.categoryName),
    visibleCategories
  });
  const products = useProducts();
  const tableHeaderConfig = useTableHeaderConfig();
  if (!products || products.length === 0 || featureCategories.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsx(StyledWrapper, {
    children: /*#__PURE__*/_jsxs(View, {
      backgroundColor: "fill-surface-default",
      inlineSize: "100%",
      ref: featureTableRef,
      children: [featureCategories.map(featureCategory => /*#__PURE__*/_jsx(CategorizedFeatures, {
        category: featureCategory,
        focusedCategory: focusedCategory,
        tableHeaderConfig: tableHeaderConfig,
        onIntersectionChange: isIntersecting => {
          const categoryName = featureCategory.category.categoryName;
          if (isIntersecting) {
            setVisibleCategories(prevState => [...prevState, categoryName]);
          } else {
            setVisibleCategories(prevState => prevState.filter(category => category !== categoryName));
          }
        }
      }, featureCategory.category.id)), /*#__PURE__*/_jsx(ProductServiceCatalogPrompt, {
        skuCategoryName: skuCategoryName
      })]
    })
  });
}